import * as React               from "react";

import { useTheme             } from "styled-components";

import { ArrowBackVector      } from "src/components/vector/arrow-back";
import { StickyNotesVector    } from "src/components/vector/sticky-notes";
import { PhotoGalleryVector   } from "src/components/vector/photo-gallery";
import { OpenCameraVector     } from "src/components/vector/open-camera";
import { ExpandMoreVector     } from "src/components/vector/expand-more";

import { RadioGroupItem       } from "src/components/common/radio-group";
import { Button               } from "src/components/common/button";
import { Typography           } from "src/components/common/typography";
import { ButtonWithIcon       } from "src/components/common/button-with-icon";

import { NavBar               } from "src/components/features/common/nav-bar";
import { Toolbar              } from "src/components/features/main/common/toolbar";
import { AttentionDialog      } from "src/components/features/main/attention-dialog";
import { ProductTypeDialog    } from "src/components/features/main/product-type-dialog";
import { IconsKeys,
         PRODUCT_TYPE_ICONS,  } from "src/components/features/main/product-type-dialog/component";

import { Screen,
         ScreenBody,
         layout_light,
         layout_dark,
         SelectedProduct,
         TitleContainer,
         ButtonLayout,
         ButtonContainer,
         ActionContainer,
         IconContainer,
         ProductCounterLayout } from "src/containers/main/product-counter/layout";

import { Urls                 } from "src/providers/routing";
import { useCamera            } from "src/providers/camera-input-provider";

import { useAppDispatch,
         storeApi,
         useAppSelector,
         AppDispatch,         } from "src/store";



type ProductCounterScreenElement = {
  layout: ProductCounterLayout;
  dispatch: AppDispatch
};

const Navigation: React.FC<ProductCounterScreenElement> = ({ layout, dispatch }) => {
  return (
    <React.Fragment>
      <NavBar
        variant = { layout.navbarVariant }
        label = 'Счетчик продукции'
        startButtons={[
          {
            id: "back",
            vector: <ArrowBackVector />,
            action: () => {
              dispatch(storeApi.main.mainProductCountResultDialog.action.resetState());
              Urls.MainServices.build().navigate()
            },
          },
        ]}
      />
    </React.Fragment>
  );
};

type TypeOfProductProps = { productType: RadioGroupItem };
const TypeOfProduct: React.FC<TypeOfProductProps> = ({ productType }) => {
  const theme = useTheme();
  const layout = theme.colorScheme === "light" ? layout_light : layout_dark;
  const dispatch = useAppDispatch();

  const handleTap = () => {
    dispatch(storeApi.main.mainProductTypeDialog.action.setIsOpened());
  };

  return (
    <React.Fragment>
      <ButtonLayout layout = { layout }>
        <SelectedProduct layout = { layout }>
          <IconContainer layout = { layout }>
            { productType?.iconKey
              ? PRODUCT_TYPE_ICONS[productType?.iconKey as IconsKeys]
              : null }
          </IconContainer>
          <TitleContainer>
            <Typography variant = 'steelw400s12lh16'>Вид продукции</Typography>
            <Typography variant = 'blackw400s14lh20'>
              {productType.label}
            </Typography>
          </TitleContainer>
        </SelectedProduct>
        <Button
          variant = 'h48lightblueoutline'
          onTap = { handleTap }
          label = 'Выбрать вид продукции'
        />
      </ButtonLayout>
    </React.Fragment>
  );
};

const ChoosePhotoButton: React.FC = () => {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  
  const dispatch = useAppDispatch();
    
  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0)
    {
      return;
    }

    const file = files[0];

    if (!file.type.startsWith("image/"))
    {
      return;
    }

    dispatch(storeApi.main.mainProductCounter.action.setPhoto(file));

    Urls.MainCameraForCount.build().navigate();
  };

  const onChooseClick = () => {
    dispatch(storeApi.main.mainProductCounter.action.initState());
    fileInputRef.current && fileInputRef.current.click();
  };

  return (
    <React.Fragment>
      <ButtonWithIcon
        variant = 'h64greyoutline'
        label = 'Выбрать фото из галереи'
        leftSideIcon = { <PhotoGalleryVector /> }
        rightSideIcon={<ExpandMoreVector />}
        onTap = { onChooseClick }
      />
      <input
        ref = { fileInputRef }
        type='file'
        accept = { 'image/*' }
        style = { { display: 'none' } }
        onChange={ (e) => handleFiles(e.target.files) }
        multiple = { false }
      />
    </React.Fragment>
  );
};

const OpenCameraButton: React.FC = () => {
  const { openCamera } = useCamera();

  const dispatch = useAppDispatch();

  const handleTap = () => {
    dispatch(storeApi.main.mainProductCounter.action.initState());
    
    openCamera((file) => {
      dispatch(storeApi.main.mainProductCounter.action.setPhoto(file));
      Urls.MainCameraForCount.build().navigate();
    });
  };

  return (
    <ButtonWithIcon
      variant='h64greyoutline'
      label='Открыть камеру'
      leftSideIcon={<OpenCameraVector />}
      rightSideIcon={<ExpandMoreVector />}
      onTap={handleTap}
    />
  );
};

const PhotoRequirementsAndRecommendations: React.FC = () => {
  const handleTap = () => {
    Urls.MainRequirementsAndRecommendations.build().navigate();
  };

  return (
    <ButtonWithIcon
      variant = 'h70greyoutline'
      label = 'Требования к фото и рекомендации по съемке'
      leftSideIcon = { <StickyNotesVector /> }
      onTap = { handleTap }
    />
  );
};

export const ProductCounterScreen = () => {
  const theme = useTheme();
  const layout = theme.colorScheme === "light" ? layout_light : layout_dark;
  const productTypeValue = useAppSelector(
    storeApi.main.mainProductTypeDialog.selectors.selectProductTypeRadioItem
  );
  const isOpened = useAppSelector(
    storeApi.main.mainAttentionDialog.selectors.selectIsOpened
  );
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    dispatch(storeApi.main.mainAttentionDialog.action.initDialog(true));
  }, []);

  return (
    <Screen layout = { layout }>
      <Navigation layout = { layout } dispatch = { dispatch }/>
      <ScreenBody layout = { layout }>
        <ActionContainer layout = { layout }>
          <TypeOfProduct productType = { productTypeValue } />
          <ButtonContainer layout = { layout }>
            <ChoosePhotoButton />
            <OpenCameraButton />
          </ButtonContainer>
        </ActionContainer>
        <PhotoRequirementsAndRecommendations />
      </ScreenBody>
      <Toolbar variant = { layout.toolbarVariant } activeItem = 'services' />
      <AttentionDialog isOpened = { isOpened } variant = 'attention_dialog_main' />
      <ProductTypeDialog  variant = 'product_dialog_main'/>
    </Screen>
  );
};
